import { useState, useMemo, useCallback } from 'react'
import { useDebouncedCallback } from 'use-debounce'
import { useAnalysis } from 'src/api/analysis/list'

const EXCLUDED_ANALYSIS_TYPES = ['GENESUS-COHORT', 'EXOMEDEPTH-COHORT', 'CARRIER']
const PAGE_SIZE = 100

/**
 * Shared hook for multi-sample analysis sample selection.
 * Uses server-side sample_name search with debounce and a reasonable page size
 * instead of fetching the entire analysis list. Excluded analysis types
 * (cohort, carrier) are filtered client-side from the paginated results.
 *
 * @param {Object} opts
 * @param {string} opts.initialSearch - Initial search term (default: '')
 * @returns {{ options, isLoading, isError, isFetching, searchTerm, setSearchTerm, totalCount }}
 */
const useMultiSampleOptions = ({ initialSearch = '' } = {}) => {
  const [searchTerm, setSearchTerm] = useState(initialSearch)
  const [debouncedSearch, setDebouncedSearch] = useState(initialSearch)

  const debouncedSetSearch = useDebouncedCallback((value) => {
    setDebouncedSearch(value)
  }, 300)

  const handleSearchChange = useCallback(
    (value) => {
      setSearchTerm(value)
      debouncedSetSearch(value)
    },
    [debouncedSetSearch],
  )

  const { query } = useAnalysis({
    page: 0,
    limit: PAGE_SIZE,
    sample_name: debouncedSearch || undefined,
  })

  const { data: analysisData, isLoading, isError, isFetching } = query

  const options = useMemo(() => {
    if (!analysisData?.results) {
      return []
    }

    return analysisData.results
      .filter((analysis) => !EXCLUDED_ANALYSIS_TYPES.includes(analysis.analysis_type))
      .map((analysis) => ({
        id: analysis.file_id,
        label: analysis.sample_name,
        analysisType: analysis.analysis_type,
        tags: analysis.tags || [],
        completed: analysis.status === 'DONE',
      }))
  }, [analysisData])

  const totalCount = analysisData?.pagination?.total_count ?? 0

  // isSearching is true ONLY when this instance is actively searching:
  // either the debounce hasn't fired yet, or the fetch for the search is in progress.
  // Idle instances (searchTerm === '') never show as searching, even if
  // react-query's isFetching is true due to background refetch intervals.
  const isSearching = searchTerm !== '' && (searchTerm !== debouncedSearch || isFetching)

  return {
    options,
    isLoading,
    isError,
    isFetching,
    isSearching,
    searchTerm,
    setSearchTerm: handleSearchChange,
    totalCount,
  }
}

export default useMultiSampleOptions
